import * as React from "react";
import { cn } from "lib/utils";
import { Skeleton } from "components/ui/skeleton";

/**
 * Shared loading scaffolds.
 *
 * The rule these encode: a loading screen should be the *shape* of the content
 * that is coming, not a spinner in the middle of an empty page. Every one of
 * these mirrors the real layout it stands in for, so nothing jumps when data
 * lands.
 */

export const SkeletonText: React.FC<{ lines?: number; className?: string; lastWidth?: string }> = ({
    lines = 3,
    className,
    lastWidth = "60%",
}) => (
    <div className={cn("space-y-2", className)} aria-hidden="true">
        {Array.from({ length: lines }).map((_, i) => (
            <Skeleton
                key={i}
                className="h-3.5 rounded"
                style={i === lines - 1 ? { width: lastWidth } : { width: "100%" }}
            />
        ))}
    </div>
);

export const SkeletonStats: React.FC<{ count?: number; className?: string }> = ({
    count = 4,
    className,
}) => (
    <div
        className={cn(
            "grid gap-3",
            count === 3 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 lg:grid-cols-4",
            className
        )}
        aria-hidden="true"
    >
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="rounded-xl bg-surface border border-line shadow-card px-4 pt-4 pb-3.5">
                <div className="flex items-start gap-2 mb-3">
                    <Skeleton className="h-6 w-6 rounded-md flex-shrink-0" />
                    <Skeleton className="h-3.5 flex-1 max-w-[6rem] rounded mt-1" />
                </div>
                <Skeleton className="h-7 w-16 rounded" />
            </div>
        ))}
    </div>
);

export const SkeletonTable: React.FC<{ rows?: number; columns?: number; className?: string }> = ({
    rows = 8,
    columns = 5,
    className,
}) => (
    <div
        className={cn("rounded-xl bg-surface border border-line shadow-card overflow-hidden", className)}
        aria-hidden="true"
    >
        <div className="flex items-center gap-4 px-4 h-11 bg-surface-sunken border-b border-line">
            {Array.from({ length: columns }).map((_, i) => (
                <Skeleton key={i} className={cn("h-3 rounded", i === 0 ? "w-1/4" : "flex-1")} />
            ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
            <div key={r} className="flex items-center gap-4 px-4 h-14 border-b border-line-subtle last:border-0">
                {Array.from({ length: columns }).map((_, c) => (
                    <Skeleton key={c} className={cn("h-3.5 rounded", c === 0 ? "w-1/4" : "flex-1")} />
                ))}
            </div>
        ))}
    </div>
);

export const SkeletonCards: React.FC<{
    count?: number;
    columns?: 2 | 3 | 4;
    /** Card gets a media/thumbnail block on top */
    media?: boolean;
    className?: string;
}> = ({ count = 6, columns = 3, media = false, className }) => (
    <div
        className={cn(
            "grid gap-4",
            columns === 2
                ? "grid-cols-1 sm:grid-cols-2"
                : columns === 4
                    ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
                    : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
            className
        )}
        aria-hidden="true"
    >
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="rounded-xl bg-surface border border-line shadow-card overflow-hidden">
                {media && <Skeleton className="aspect-video w-full rounded-none" />}
                <div className="p-4 space-y-3">
                    <Skeleton className="h-4 w-2/3 rounded" />
                    <SkeletonText lines={2} />
                    <div className="flex items-center justify-between pt-1">
                        <Skeleton className="h-5 w-16 rounded-full" />
                        <Skeleton className="h-3 w-12 rounded" />
                    </div>
                </div>
            </div>
        ))}
    </div>
);

export const SkeletonList: React.FC<{ count?: number; className?: string }> = ({
    count = 6,
    className,
}) => (
    <div className={cn("rounded-xl bg-surface border border-line shadow-card divide-y divide-line-subtle", className)} aria-hidden="true">
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-4">
                <Skeleton className="h-9 w-9 rounded-xl flex-shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                    <Skeleton className="h-3.5 w-1/3 rounded" />
                    <Skeleton className="h-3 w-1/2 rounded" />
                </div>
                <Skeleton className="h-6 w-16 rounded-full flex-shrink-0" />
            </div>
        ))}
    </div>
);

export type LoadingShape = "stats" | "table" | "cards" | "list" | "text" | "page";

export interface LoadingStateProps {
    shape?: LoadingShape;
    /** Rows / cards to draw */
    count?: number;
    columns?: 2 | 3 | 4;
    media?: boolean;
    /** Announced to screen readers while the region is busy */
    label?: string;
    className?: string;
}

/**
 * Declarative loading placeholder — `<LoadingState shape="table" />` instead of
 * a bespoke pile of `animate-pulse` divs per feature.
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
    shape = "page",
    count,
    columns = 3,
    media = false,
    label = "Loading",
    className,
}) => {
    const body = (() => {
        switch (shape) {
            case "stats":
                return <SkeletonStats count={count ?? 4} />;
            case "table":
                return <SkeletonTable rows={count ?? 8} />;
            case "cards":
                return <SkeletonCards count={count ?? 6} columns={columns} media={media} />;
            case "list":
                return <SkeletonList count={count ?? 6} />;
            case "text":
                return <SkeletonText lines={count ?? 3} />;
            case "page":
            default:
                return (
                    <div className="space-y-5">
                        <div className="flex items-center gap-3.5">
                            <Skeleton className="h-10 w-10 rounded-xl flex-shrink-0" />
                            <div className="flex-1 min-w-0 space-y-2">
                                <Skeleton className="h-5 w-48 max-w-full rounded" />
                                <Skeleton className="h-3 w-64 max-w-full rounded" />
                            </div>
                        </div>
                        <SkeletonStats />
                        <SkeletonTable rows={count ?? 6} />
                    </div>
                );
        }
    })();

    return (
        <div role="status" aria-busy="true" aria-live="polite" className={className}>
            <span className="sr-only">{label}</span>
            {body}
        </div>
    );
};

export default LoadingState;
